import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  ListRow,
  ListRowGroup,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodActVsPotChip,
  SodEndPointChip,
  SodMessage,
  SodReloader,
  SodRiskPriorityChip,
} from '../../../../shared/components';
import {
  ABORT_ERROR,
  CF_NEU_BLD,
  IS_READ_ONLY,
  RENDER_MODES,
  SEARCH_TEXT_LENGTH,
} from '../../../../shared/constants';
import { useInViewObserver, useTypedSelector } from '../../../../shared/hooks';
import {
  type ListTrackerState,
  type TrackedListRowState,
} from '../../../../shared/types';
import { testIds } from '../../../../tests';
import {
  type SodViolationsListProps,
  type ViolationsTabSidePanelGroupRowContextState,
} from '../../data-layer/violationsTabTypes';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import { useSetFilterDelta } from '../../hooks';
import {
  capitalize,
  configureTracker,
  findKeyValue,
  getListPayload,
  getRowContext,
} from '../../utils';
import ViolationsListActions from '../ViolationsListActions';

import ViolationsListTabLoader from './ViolationsListTabLoader';
import { AccountChip, ColumnChip, UserChip } from './ViolationsListTabShared';

import './ViolationsTabList.css';

const MAX_COUNT = 50;
const LEAVE_ANIM_DURATION = 400;

/**
 * ViolationsTabList
 * @description A component that renders a list of violations in a tabbed interface.
 * This component is responsible for displaying and managing violations data within
 * the violations tab section of the application.
 *
 * @typedef {Object} Violation
 * @property {string} id - Unique identifier for the violation
 * @property {string} title - Title or summary of the violation
 * @property {string} description - Detailed description of the violation
 * @property {string} severity - Severity level of the violation
 * @property {Date} timestamp - When the violation occurred
 *
 * @typedef {Object} ViolationsTabListProps
 * @property {Violation[]} violations - Array of violation objects to be displayed
 * @property {boolean} isLoading - Flag indicating if violations are being loaded
 * @property {(id: string) => void} onViolationSelect - Callback function when a violation is selected
 * @property {string} [selectedViolationId] - ID of the currently selected violation
 * @property {Object} [filters] - Optional filters to apply to the violations list
 * @property {'asc' | 'desc'} [sortOrder='desc'] - Sort order for the violations list
 *
 * @example
 * ```tsx
 * <ViolationsTabList
 *   violations={violationsData}
 *   isLoading={false}
 *   onViolationSelect={(id) => handleViolationSelect(id)}
 *   selectedViolationId="violation-123"
 *   sortOrder="desc"
 * />
 * ```
 *
 * @returns {JSX.Element} A rendered list of violations in a tabbed interface
 *
 * @accessibility
 * - Uses semantic HTML elements for better screen reader navigation
 * - Implements ARIA attributes for improved accessibility
 * - Supports keyboard navigation through the violations list
 * - Maintains sufficient color contrast for all text elements
 *
 * @performance
 * - Implements virtualization for large lists
 * - Uses memoization to prevent unnecessary re-renders
 * - Optimizes data filtering and sorting operations
 */
function ViolationsTabList(): ReactElement {
  const dispatch = useDispatch();

  const {
    violationsTab: {
      controls: {
        renderMode,
        tabInAction,
        activeFilters,
        searchKey,
        sortInAction,
        activeColumns,
        activeInsight,
      },
      list: { totalCount, rowIdToTransition },
      bulkaction: { isBulkChecked, isFooterVisible },
      sidepanel: { isSidePanelVisible },
    },
    global: { isGlobalLoading },
  } = useTypedSelector((state) => state.aag);

  const { setDelta } = useSetFilterDelta();

  const [fetchViolationsList, { isLoading: isListLoading }] =
    violationsTabActions.useGetViolationsListMutation();

  // Offset, observer and lists
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [currentOffset, setCurrentOffset] = useState(0);
  const [isObserverInView, setIsObserverInView] = useState(true);
  const [isListError, setIsListError] = useState(false);
  const [listRows, setListRows] = useState([] as TrackedListRowState[]);

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        totalCount === null ||
          (currentOffset < totalCount && listRows.length < totalCount)
      ),
    [currentOffset, totalCount, listRows.length]
  );
  const isListZero = useMemo(
    () => Boolean(listRows.length === 0),
    [listRows.length]
  );
  const isZeroCount = useMemo(
    () => Boolean(isListZero && totalCount !== null && !isValidApiCall),
    [totalCount, isListZero, isValidApiCall]
  );
  const isSearchActive = useMemo(
    () => Boolean(searchKey !== null && searchKey.length >= SEARCH_TEXT_LENGTH),
    [searchKey]
  );
  const isFilterActive = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  useEffect(() => {
    dispatch(violationsTabActions.setIsListLoading(isListLoading));
  }, [dispatch, isListLoading]);

  const handleListApiCall = useCallback(
    async (offset: number) => {
      abortRef.current?.('New Search');

      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );
      const payload: SodViolationsListProps = getListPayload(
        activeFiltersMap,
        tabInAction ?? undefined,
        searchKey,
        sortInAction,
        activeInsight,
        { max: MAX_COUNT, offset }
      );

      try {
        const controller = fetchViolationsList(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        if ((error as { name?: string }).name !== ABORT_ERROR) {
          setIsListError(true);
          setIsObserverInView(false);

          throw error;
        }

        throw Error(ABORT_ERROR);
      }
    },
    [
      fetchViolationsList,
      activeFilters,
      tabInAction,
      searchKey,
      sortInAction,
      activeInsight,
    ]
  );

  // Infinite scroll effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isObserverInView || !isValidApiCall) {
        return;
      }

      Logger.log('Fetching', currentOffset, 'to', currentOffset + MAX_COUNT);

      handleListApiCall(currentOffset)
        .then((response) => {
          setIsObserverInView(false);
          setCurrentOffset((prev) => prev + MAX_COUNT);
          setIsListError(false);

          const { totalRecords, data } = response as {
            totalRecords: number;
            data: TrackedListRowState[] | null | undefined;
          };

          if (!data) {
            dispatch(violationsTabActions.setListTotalCount(0));

            return;
          }

          const newRows = configureTracker(data, isBulkChecked);

          setListRows((prev) => [
            ...prev.map((row) => ({
              ...row,
              tracker: {
                ...row.tracker,
                isFirstAddded: false,
              },
            })),
            ...newRows,
          ]);
          dispatch(violationsTabActions.setListTotalCount(totalRecords));
        })
        .catch(() => {});
    }, 10);

    return () => clearTimeout(debounceTimer);
  }, [
    isObserverInView,
    isValidApiCall,
    currentOffset,
    handleListApiCall,
    isBulkChecked,
    dispatch,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffect(() => {
    setListRows([]);

    const debounceTimer = setTimeout(() => {
      if (!isGlobalLoading) {
        dispatch(violationsTabActions.setListTotalCount(null));
        setListRows([]);
        setIsListError(false);
        setCurrentOffset(0);

        if (isSidePanelVisible) {
          dispatch(
            violationsTabActions.setIsSidePanelVisible(!isSidePanelVisible)
          );
        }
      }
    }, 20);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- change is not required when side panel is closed/opened
  }, [
    dispatch,
    isGlobalLoading,
    activeFilters,
    tabInAction,
    searchKey,
    sortInAction,
    activeInsight,
  ]);

  // Removing connectors if any when `list` mode and sidepanel closed
  useEffect(() => {
    if (renderMode === RENDER_MODES.LIST && !isSidePanelVisible) {
      setListRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        }))
      );
    }
  }, [isSidePanelVisible, renderMode]);

  // Individual checkboxes
  const handleCheckBoxChange = useCallback(
    (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = Boolean(e.target.checked);

      setListRows((prevListRows) =>
        prevListRows.map((row) =>
          row.tracker.id === id
            ? {
                ...row,
                tracker: { ...row.tracker, isChecked },
              }
            : row
        )
      );
    },
    []
  );

  // Sidepanel handling and row connector lgic on `row` click
  const handleRowClick = useCallback(
    (id: string) => {
      let modifiedIndex: number | undefined;
      const rowContext: ViolationsTabSidePanelGroupRowContextState = {
        current: { id: '' },
      };
      const listLength = listRows.length;

      const modifiedRows = listRows.map((row, index) => {
        if (row.tracker.id === id) {
          modifiedIndex = index;

          return {
            ...row,
            tracker: {
              ...row.tracker,
              isRowDetailsOpen: true,
            },
          };
        }

        return {
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        };
      });

      if (modifiedIndex !== undefined) {
        // Will always have the current row
        rowContext.current = getRowContext(modifiedRows[modifiedIndex]);

        if (modifiedIndex < listLength - 1 && listLength > 1) {
          rowContext.next = getRowContext(modifiedRows[modifiedIndex + 1]);
        }

        // First element is clicked
        if (modifiedIndex === 0 && listLength > 1) {
          rowContext.next = getRowContext(modifiedRows[modifiedIndex + 1]);
        }

        setListRows(() => modifiedRows);

        dispatch(violationsTabActions.setRowContext(rowContext));

        if (!isSidePanelVisible) {
          dispatch(
            violationsTabActions.setIsSidePanelVisible(!isSidePanelVisible)
          );
        }
      }
    },
    [dispatch, isSidePanelVisible, listRows]
  );

  // Handling checkbox based on the bulk checkbox
  useEffect(() => {
    setListRows((prevListRows) =>
      prevListRows.map((row) => ({
        ...row,
        tracker: { ...row.tracker, isChecked: isBulkChecked },
      }))
    );
  }, [isBulkChecked]);

  // Transition logic here
  useEffect(() => {
    if (rowIdToTransition !== null) {
      const nextItem: { tracker?: ListTrackerState } = {};
      const listRowsLength = listRows.length;

      // Start the transition
      setListRows((prevListRows) =>
        prevListRows.map((row, index) => {
          if (row.tracker.id === rowIdToTransition) {
            // Check if next available item is present
            if (index !== listRowsLength - 1 && row.tracker.isRowDetailsOpen) {
              nextItem.tracker = listRows[index + 1].tracker;
            }

            return {
              ...row,
              tracker: { ...row.tracker, isTransition: true },
            };
          }

          return row;
        })
      );

      // End the transition and manage the next item with sidepanel
      setTimeout(() => {
        setListRows((prevListRows) =>
          prevListRows
            .filter((row) => row.tracker.id !== rowIdToTransition)
            .map((row) => {
              if (nextItem.tracker && row.tracker.id === nextItem.tracker.id) {
                return {
                  ...row,
                  tracker: { ...row.tracker, isRowDetailsOpen: true },
                };
              }

              return row;
            })
        );

        dispatch(violationsTabActions.setListTransitionId(null));
      }, LEAVE_ANIM_DURATION);
    }
  }, [dispatch, rowIdToTransition, listRows]);

  // Clear filter on no data
  const handleClearBtn = useCallback(() => {
    setDelta(activeFilters, []);
  }, [activeFilters, setDelta]);

  // Render body here
  const renderListBody = useCallback(
    () => {
      // No data Screens
      if (isZeroCount) {
        return (
          <SodMessage
            isSearchActive={isSearchActive}
            isFilterActive={isFilterActive}
            handleFilterClearBtn={handleClearBtn}
            defaultTitle='No Open Violations'
            defaultMessage='Keep up the good work!'
          />
        );
      }

      return (
        <ListRowGroup className='Sod-ViolationsTabList--isListRowGroup'>
          {/* List rows */}
          {listRows.map((row) => {
            const {
              actualVsPotential,
              risk: { riskName, riskPriority, riskScore },
              user: { firstName, lastName, username },
              tracker: {
                id,
                isChecked,
                isRowDetailsOpen,
                isTransition,
                isFirstAddded,
              },
              accounts,
              endpoints,
            } = row;

            const rowContext = getRowContext(row);
            const accountCount = accounts.length;

            const rowClasses = cx(
              'Sod-ViolationsTabList-listRow',
              isRowDetailsOpen && 'Sod-ViolationsTabList-listRow--isConnected',
              isTransition && 'Sod-ViolationsTabList-listRow--isTransition',
              isFirstAddded && 'Sod-ViolationsTabList-listRow--isFirstAddded'
            );

            const selectedColumns = activeColumns.filter(
              (column) => column.isChecked
            );

            const listDefaultColoumns = [
              <SodRiskPriorityChip
                key='SodRiskPriorityChip'
                priority={riskPriority}
                riskScore={riskScore}
              />,
              <SodActVsPotChip
                key='SodActVsPotChip'
                value={actualVsPotential}
              />,
              accountCount > 0 && (
                <SodEndPointChip
                  key='SodEndPointChip'
                  endpoints={endpoints.map((endpoint) => ({
                    endpointName: endpoint.endpointName,
                    endpointLogo: endpoint.endpointLogo,
                  }))}
                />
              ),
              <UserChip
                key='UserChip'
                firstName={firstName}
                lastName={lastName}
                userName={username}
              />,
              accountCount > 0 && (
                <AccountChip
                  key='AccountChip'
                  visibleAccountName={accounts[0].accountName}
                  accountCount={accountCount}
                  isPrefixInterpoint={Boolean(selectedColumns.length)}
                />
              ),
            ];

            const listConfigurableColumns = selectedColumns.map(
              (column, index) => {
                const { label } = column;
                let { value } = column;

                let columnValue = '';

                if (value === 'userStatus') {
                  columnValue = capitalize(row.user.status) || '-';
                } else {
                  if (value === 'rulesetName') {
                    value = 'ruleset';
                  }

                  columnValue = findKeyValue(row, value);
                }

                return (
                  <ColumnChip
                    key={value}
                    name={label}
                    value={columnValue}
                    isPrefixInterpoint={index < selectedColumns.length - 1}
                  />
                );
              }
            );

            const allColumnsToRender = [
              ...listDefaultColoumns,
              ...listConfigurableColumns,
            ];

            return (
              <ListRow
                key={id}
                className={rowClasses}
                dataTestId={testIds.SOD_VIOLATIONS_TAB_LIST_ROW}
                isCheckboxDisabled
                leadingComp={<ApplicationIcon logo='SOD' size='medium' />}
                centerContent={
                  <Box className='Sod-ViolationsTabList-centerContent'>
                    <Typography
                      kind='h3'
                      desktop='h3-d'
                      mobile='h3-d'
                      color={CF_NEU_BLD}>
                      {riskName}
                    </Typography>
                    <Box className='Sod-ViolationsTabList-centerContentStatuses'>
                      {allColumnsToRender}
                    </Box>
                  </Box>
                }
                trailingContent={
                  !isSidePanelVisible && (
                    <ViolationsListActions
                      tabInAction={tabInAction}
                      isDisabled={isFooterVisible || IS_READ_ONLY}
                      rowContext={rowContext}
                    />
                  )
                }
                isChecked={isChecked}
                handleCheckBoxChange={handleCheckBoxChange(id)}
                handleRowClick={() => handleRowClick(id)}
                isRowDetailsOpen={isRowDetailsOpen}
                connectorDistance='3.125rem'
                isRowConnectTransitionEnabled
              />
            );
          })}
          {/* Observer Element */}
          {!isListError ? listObserverElement : null}
          {/* Loading Skeleton */}
          {!isListError && isValidApiCall && (
            <ViolationsListTabLoader count={3} />
          )}
          {/* Retry mechanism */}
          {isListError && (
            <SodReloader triggerRefresh={() => setIsListError(false)} />
          )}
        </ListRowGroup>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- invertedLocalize function will cause rerenders
    [
      handleCheckBoxChange,
      handleRowClick,
      listObserverElement,
      isFooterVisible,
      isSidePanelVisible,
      listRows,
      tabInAction,
      isValidApiCall,
      isZeroCount,
      isSearchActive,
      isFilterActive,
      isListError,
    ]
  );

  return (
    <Box
      className='Sod-ViolationsTabList'
      dataTestId={testIds.SOD_VIOLATIONS_TAB_LIST}>
      {renderListBody()}
    </Box>
  );
}

export default memo(ViolationsTabList);
